import React, { useState, useEffect } from "react";
import { Container, Box, Typography, Avatar, Chip, Divider, CircularProgress } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PolicyIcon from "@mui/icons-material/Policy";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import { endSession } from "../session";
import { useNavigate } from "react-router-dom";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import logo from "../imgs/logo192.png";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import Header from "./Header2";
import ProfileMenuItem from "../components/ProfileMenuItem";

const DEFAULT_AVATAR = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="32" fill="#161b2e"/><circle cx="32" cy="24" r="11" fill="#2a3a5c"/><ellipse cx="32" cy="54" rx="18" ry="12" fill="#2a3a5c"/></svg>')}`;

const Perfil = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) setUserData({ uid: user.uid, email: user.email, ...snap.data() });
        } catch (e) {
          console.error(e);
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const onLogout = () => { endSession(); navigate("/login"); };

  const menuItems = [
    { href: "/edit", icon: <EditIcon />, text: "Editar Perfil" },
    { href: "/terms", icon: <PolicyIcon />, text: "Términos de Servicio" },
    { href: "/privacy", icon: <PrivacyTipIcon />, text: "Política y Privacidad" },
    { onClick: onLogout, icon: <ExitToAppIcon />, text: "Cerrar sesión", color: "red" },
  ];

  const glass = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    backdropFilter: "blur(12px)",
    padding: "20px",
    marginBottom: "16px",
  };

  const infoRow = (icon, text) => text ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
      <Box sx={{ color: "#00d4ff", display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.9rem", color: "rgba(200,215,255,0.75)" }}>{text}</Typography>
    </Box>
  ) : null;

  return (
    <Container>
      <Header />
      <Container sx={{ mt: "84px", pb: 14, px: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>
        ) : userData ? (
          <>
            {/* Hero card */}
            <Box sx={{ ...glass, display: "flex", flexDirection: "column", alignItems: "center", pt: 3, pb: 2.5 }}>
              <Avatar
                src={userData.profileImageUrl || DEFAULT_AVATAR}
                alt={userData.displayName}
                sx={{ width: 88, height: 88, border: "3px solid rgba(0,212,255,0.45)", boxShadow: "0 0 24px rgba(0,212,255,0.25)", mb: 1.5, borderRadius: "22px" }}
              />
              <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", color: "#e6edf3", letterSpacing: "-0.3px" }}>
                {userData.displayName}
              </Typography>
              {userData.profession && (
                <Chip label={userData.profession} size="small" sx={{ mt: 0.8, background: "rgba(0,212,255,0.12)", border: "1px solid rgba(0,212,255,0.3)", color: "#00d4ff", fontWeight: 600, fontSize: "0.78rem" }} />
              )}
              {userData.experience && (
                <Typography sx={{ mt: 0.8, fontSize: "0.78rem", color: "rgba(255,255,255,0.35)", letterSpacing: "1px", textTransform: "uppercase" }}>
                  {userData.experience} de experiencia
                </Typography>
              )}
            </Box>

            {/* Contact info */}
            <Box sx={glass}>
              <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)", letterSpacing: "2px", textTransform: "uppercase", mb: 1.5 }}>Contacto</Typography>
              {infoRow(<PhoneIcon fontSize="small" />, userData.phone)}
              {infoRow(<EmailIcon fontSize="small" />, userData.email)}
              {infoRow(<LocationOnIcon fontSize="small" />, userData.point)}
              {infoRow(<LinkIcon fontSize="small" />, userData.website)}
              {infoRow(<LinkedInIcon fontSize="small" />, userData.linkedin)}
            </Box>

            {/* Curriculum / bio */}
            {userData.bio && (
              <Box sx={glass}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <DescriptionIcon sx={{ color: "#00d4ff", fontSize: 18 }} />
                  <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)", letterSpacing: "2px", textTransform: "uppercase" }}>Curriculum / Bio</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.92rem", color: "rgba(200,215,255,0.75)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {userData.bio}
                </Typography>
              </Box>
            )}

            <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2 }} />

            {/* Settings menu */}
            {menuItems.map((item, i) => (
              <ProfileMenuItem key={i} href={item.href} icon={item.icon} text={item.text} color={item.color} onClick={item.onClick} />
            ))}
          </>
        ) : null}
      </Container>
      <FloatingWhatsApp
        phoneNumber="+542323680787"
        placeholder="Escribe un mensaje..."
        statusMessage="Responde en menos de 10 minutos"
        accountName="FindMe Soporte"
        avatar={logo}
        chatMessage="Hola!¿Cómo estás?¿En que puedo ayudarte?😃"
      />
    </Container>
  );
};

export default Perfil;
